import { sortByKey } from '@semios/app-platform-common'
import { CompareSeasonsSelect } from 'App/Map/PanelDetails/StackemCharts/CompareSeasonsSelect/CompareSeasonsSelect'
import {
  BlocksGroupedByCropPicker,
  shapeBlocksToOptions,
} from 'components/BlocksGroupedByCropPicker/BlocksGroupedByCropPicker'
import { DateRangePicker } from 'components/DateRangePicker/DateRangePicker'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'

const canViewSprayRecordOnProperty = (propertyId: number) => {
  return checkAuthorization({
    permission: 'VIEW_GROWER_SPRAYS',
    entity: propertyId,
  })
}

export const SprayRecordFilter = ({
  propertyId,
  dateFrom,
  dateTo,
  timezone,
  selectedBlocks,
  onSelectedBlocks,
  canViewSprayRecordOnSelectedProperty,
  isVisible,
}: {
  propertyId: number
  dateFrom: string
  dateTo: string
  timezone: string
  selectedBlocks: TOptions[]
  onSelectedBlocks?: (blocks: TOptions[]) => void
  canViewSprayRecordOnSelectedProperty: boolean
  isVisible: boolean
}) => {
  const now = moment.tz(new Date(), timezone)

  const propertiesData = useMemo(() => {
    const result = Object.values(fieldAssetStore.getState().properties || {})
      .filter((property) => canViewSprayRecordOnProperty(property.propertyId))
      .map((property) => ({
        id: property.propertyId,
        value: String(property.propertyId),
        label: property.propertyName,
      }))

    return result.sort(sortByKey('label'))
  }, [])

  const blocksData = useMemo(() => {
    if (!canViewSprayRecordOnSelectedProperty) return []

    return shapeBlocksToOptions(
      Object.values(fieldAssetStore.getState().properties?.[propertyId]?.blocks || {}),
    )
  }, [propertyId])

  // select all blocks when selected property changes
  useEffect(() => {
    if (!canViewSprayRecordOnSelectedProperty) {
      onSelectedBlocks?.([])

      return
    }

    onSelectedBlocks?.(blocksData)
  }, [propertyId])

  // ensure future dates are not selected
  useEffect(() => {
    if (!isVisible) return

    if (now < moment.tz(dateTo, timezone)) {
      detailsPanelStore.setState((s) => ({
        ...s,
        dateTo: getIsoWithoutTheZ(now.endOf('day')),
      }))
    }
  }, [isVisible, dateTo])

  useEffect(() => {
    if (!isVisible) return

    if (now < moment.tz(dateFrom, timezone)) {
      detailsPanelStore.setState((s) => ({
        ...s,
        dateTo: getIsoWithoutTheZ(now.endOf('day')),
      }))
    }
  }, [isVisible, dateFrom])

  return (
    <div
      css={{
        'containerType': 'inline-size',
        'display': 'grid',
        'columnGap': '10px',
        'rowGap': '8px',
        'flexGrow': 100,
        'gridTemplateColumns': 'repeat(auto-fill, minmax(250px, 1fr))',
        'zIndex': SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX,
        '@container (min-width: 510px)': {
          '& > div:nth-of-type(1)': {
            order: 1,
          },
          '& > div:nth-of-type(2)': {
            order: 3,
          },
          '& > div:nth-of-type(3)': {
            order: 2,
          },
          '& > div:nth-of-type(4)': {
            order: 4,
          },
        },
        '@container (min-width: 780px)': {
          '& > div:nth-of-type(1)': {
            order: 1,
          },
          '& > div:nth-of-type(2)': {
            order: 4,
          },
          '& > div:nth-of-type(3)': {
            order: 2,
          },
          '& > div:nth-of-type(4)': {
            order: 3,
          },
        },
      }}
    >
      {/* one wrapper per filter, so the grid's nth-of-type ordering counts only these and not the
          dropdown nodes that Select and DateRangePicker mount beside their inputs */}
      <div>
        <SelectDropdown
          data={propertiesData}
          value={String(propertyId)}
          placeholder={translate.phrases.banyanApp('Property Name')}
          onChange={(propertyId) => {
            if (propertyId) setSelectedFieldAsset({ property: Number(propertyId) })
          }}
          filter={mantineSelectSearchFilterProp}
          searchable={true}
          styles={{
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
            input: {
              cursor: 'pointer',
              height: 42,
              borderColor: colors.midnight,
            },
          }}
        />
      </div>

      <div>
        <BlocksGroupedByCropPicker
          blocks={blocksData}
          selectedBlocks={selectedBlocks}
          searchBoxCss={{
            borderColor: colors.midnight,
            height: 42,
          }}
          setSelectedBlocks={function (selectedBlocks: TOptions[]): void {
            onSelectedBlocks?.(selectedBlocks)
          }}
        />
      </div>

      <div>
        <DateRangePicker
          dateFrom={moment.tz(dateFrom, timezone)}
          dateTo={moment.tz(dateTo, timezone)}
          maxDate={now}
          handleApply={(args) => {
            if (!args.value) return

            const [dateFrom, dateTo] = args.value

            if (!dateFrom || !dateTo) return

            detailsPanelStore.setState((s) => ({
              ...s,
              compareSeasonsInterval: 0,
              dateFrom: getIsoWithoutTheZ(moment.tz(dateFrom, timezone).startOf('day')),
              dateTo: getIsoWithoutTheZ(moment.tz(dateTo, timezone).endOf('day')),
            }))
          }}
          height={42}
          hasResponsiveWidth={true}
          timezone={timezone}
        />
      </div>

      <div>
        <CompareSeasonsSelect hasResponsiveWidth={true} />
      </div>
    </div>
  )
}
